/**
 * Helper functions
 *  Map --> Helper to update or add a source + layer
 */
import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import "./MapComponent.css";
import config from "../../../../config";
import { getEventIcon } from "modules/eventIcons";
import { FormControlLabel, Switch } from "@mui/material";

mapboxgl.accessToken = config.MAPBOX_ACCESS_TOKEN_3D;

// Normalize category.name to be icon-safe
const normalizeIconName = (name) =>
  name?.toLowerCase().replace(/\s+/g, "-").trim();

const safeParse = (val) => {
  try {
    return typeof val === "string" ? JSON.parse(val) : val;
  } catch {
    return val;
  }
};

const getPopupCoordinates = (feature) => {
  if (!feature || !feature.geometry) return null;

  const { geometry } = feature;

  if (geometry.type === "Point" && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.slice(); // defensive copy
  }

  if (
    geometry.type === "LineString" &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length > 0
  ) {
    const coordsArray = geometry.coordinates;
    const midIndex = Math.floor(coordsArray.length / 2);
    return coordsArray[midIndex];
  }

  console.warn("Invalid or unsupported geometry:", geometry);
  return null;
};

function MapComponent({ events, selectedEvent }) {
  console.log("MapComponent [events]: ", events);
  console.log(
    "MapComponent [LineString event_ids]: ",
    Array.isArray(events)
      ? events
          .filter((event) => event?.geometry?.type === "LineString")
          .map((event) => event.id)
      : []
  );

  const map3D = config.MAP_3D;
  const map2D = "mapbox://styles/mapbox/dark-v11";

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const mapLoadedRef = useRef(false); // ✅ ξέρουμε αν έχει φορτώσει το style
  const lastSelectedIdRef = useRef(null);
  const [selectedMapStyle, setSelectedMapStyle] = useState(map3D);

  // ----- Popup message helpers -----
  const displayHoverMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div>
        ${category?.name ? `Category: ` : ""}${
      category?.name || ""
    }${subcategory?.name ? ` (${subcategory.name})` : ""}<br />
        ${type_of_event?.name ? `Type: ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `Source: (${source.name})` : ""}<br/>   
      </div>
    `;
  };

  const displayClickMessage = (feature) => {
    const props = feature.properties;
    const category = safeParse(props.category);
    const subcategory = safeParse(props.subcategory);
    const source = safeParse(props.source);
    const type_of_event = safeParse(props.type_of_event);

    return `
      <div class="custom-popup-content">
        ${category?.name ? `Category: ` : ""}${
      category?.name || ""
    }${subcategory?.name ? ` (${subcategory.name})` : ""}<br />
        ${type_of_event?.name ? `Type: ${type_of_event.name}` : ""}<br/>
        ${source?.name ? `Source: (${source.name})` : ""}<br/>   
      </div>
    `;
  };

  // set and reset FeatureState
  const resetFeatureState = (map, source, id, stateKey) => {
    if (id !== null && id !== undefined) {
      map.setFeatureState({ source, id }, { [stateKey]: false });
    }
  };

  const setFeatureState = (map, source, id, stateKey) => {
    if (id !== null && id !== undefined) {
      map.setFeatureState({ source, id }, { [stateKey]: true });
    }
  };

  // ---------- Render layers από events ----------
  const renderEventLayers = (map) => {
    if (!map || !events || events.length === 0) return;

    // 2. Organize features
    const pointFeatures = [];
    const lineFeatures = [];
    const lineSymbolFeatures = [];

    const uniqueIcons = new Map();

    // 3. Create features ανά geometry
    events.forEach((event) => {
      const geom = event.geometry;
      if (!geom) return;

      const iconName = normalizeIconName(event.category?.name);
      const iconPath = getEventIcon(event.category?.id);

      // Exclude nested geometry from properties
      const { geometry, ...eventWithoutGeometry } = event;

      const feature = {
        id: event.id,
        type: "Feature",
        geometry: {
          type: geom.type,
          coordinates:
            geom.type === "Point" ? geom.coordinates[0] : geom.coordinates,
        },
        properties: {
          ...eventWithoutGeometry,
          icon: iconName,
        },
      };

      if (geom.type === "Point") {
        pointFeatures.push(feature);
        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }
      } else if (geom.type === "LineString") {
        lineFeatures.push(feature);

        if (!uniqueIcons.has(iconName)) {
          uniqueIcons.set(iconName, iconPath);
        }

        // Create a symbol at the first coordinate of the line
        lineSymbolFeatures.push({
          id: `${event.id}`,
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: geom.coordinates[0],
          },
          properties: {
            ...eventWithoutGeometry,
            icon: iconName,
            isLine: true,
          },
        });
      }
    });

    // 4. Load unique icons
    uniqueIcons.forEach((path, name) => {
      if (!map.hasImage(name)) {
        map.loadImage(path, (err, image) => {
          if (err) {
            console.error("Error loading icon:", err);
            return;
          }
          if (!map.hasImage(name)) {
            map.addImage(name, image);
          }
        });
      }
    });

    // Helper to update or add a source + layer
    const updateOrAdd = (sourceId, layerId, layerConfig, data) => {
      const existingSource = map.getSource(sourceId);

      if (existingSource) {
        existingSource.setData(data);
      } else {
        map.addSource(sourceId, {
          type: "geojson",
          data,
        });

        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            source: sourceId,
            ...layerConfig,
          });
        }
      }
    };

    // 5a. Points layer
    updateOrAdd(
      "events-point",
      "events-point-layer",
      {
        type: "symbol",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
        paint: {
          "icon-opacity": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            1.5,
            1,
          ],
        },
      },
      {
        type: "FeatureCollection",
        features: pointFeatures,
      }
    );

    // 5b. LineString layer
    updateOrAdd(
      "events-lineString",
      "events-lineString-layer",
      {
        type: "line",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-emissive-strength": 1.0,
          "line-color": "#b33b32",
          "line-width": 1,
          "line-opacity": 0.5,
        },
      },
      {
        type: "FeatureCollection",
        features: lineFeatures,
      }
    );

    // 5c. Symbol on lines (icons πάνω στις γραμμές)
    updateOrAdd(
      "events-lineStringSymbol",
      "events-lineStringSymbol-layer",
      {
        type: "symbol",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 0.9,
        },
      },
      {
        type: "FeatureCollection",
        features: lineSymbolFeatures,
      }
    );

    // 6. Hover popup – το στήνουμε μόνο μία φορά
    if (!map.__eventsHoverAndClickSetup) {
      map.__eventsHoverAndClickSetup = true;

      const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });

      const hoverLayers = [
        "events-point-layer",
        "events-lineStringSymbol-layer",
      ];

      let hoveredPointId = null;
      let hoveredLineId = null;

      hoverLayers.forEach((layerId) => {
        map.on("mouseenter", layerId, (e) => {
          map.getCanvas().style.cursor = "pointer";

          const feature = e.features && e.features[0];
          if (!feature) return;

          const coordinates = getPopupCoordinates(feature);
          if (!coordinates) return;

          const content = displayHoverMessage(feature);

          if (layerId === "events-point-layer") {
            resetFeatureState(map, "events-point", hoveredPointId, "hover");
            hoveredPointId = feature.id;
            setFeatureState(map, "events-point", hoveredPointId, "hover");
          }

          if (layerId === "events-lineStringSymbol-layer") {
            resetFeatureState(
              map,
              "events-lineStringSymbol",
              hoveredLineId,
              "hover"
            );
            hoveredLineId = feature.id;
            setFeatureState(
              map,
              "events-lineStringSymbol",
              hoveredLineId,
              "hover"
            );
          }

          hoverPopup.setLngLat(coordinates).setHTML(content).addTo(map);
        });

        map.on("mouseleave", layerId, () => {
          map.getCanvas().style.cursor = "";
          hoverPopup.remove();

          if (layerId === "events-point-layer" && hoveredPointId !== null) {
            resetFeatureState(map, "events-point", hoveredPointId, "hover");
            hoveredPointId = null;
          }

          if (
            layerId === "events-lineStringSymbol-layer" &&
            hoveredLineId !== null
          ) {
            resetFeatureState(
              map,
              "events-lineStringSymbol",
              hoveredLineId,
              "hover"
            );
            hoveredLineId = null;
          }
        });
      });

      // 8. Click popup
      const clickPopup = new mapboxgl.Popup({
        className: "my-custom-popup",
        closeButton: true,
        closeOnClick: true,
      });

      const clickLayers = [
        "events-point-layer",
        "events-lineStringSymbol-layer",
      ];

      let clickedPointId = null;
      let clickedLineId = null;

      clickLayers.forEach((layerId) => {
        map.on("click", layerId, (e) => {
          const feature = e.features && e.features[0];
          if (!feature || !feature.id) return;

          const coordinates = getPopupCoordinates(feature);
          if (!coordinates) return;

          const content = displayClickMessage(feature);

          // Reset previous clicked states
          if (clickedPointId && layerId === "events-point-layer") {
            resetFeatureState(map, "events-point", clickedPointId, "click");
          }

          if (clickedLineId && layerId === "events-lineStringSymbol-layer") {
            resetFeatureState(
              map,
              "events-lineStringSymbol",
              clickedLineId,
              "click"
            );
          }

          // Set new clicked states
          if (layerId === "events-point-layer") {
            clickedPointId = feature.id;
            setFeatureState(map, "events-point", clickedPointId, "click");
          }

          if (layerId === "events-lineStringSymbol-layer") {
            clickedLineId = feature.id;
            setFeatureState(
              map,
              "events-lineStringSymbol",
              clickedLineId,
              "click"
            );
          }

          clickPopup.setLngLat(coordinates).setHTML(content).addTo(map);
        });
      });
    }
  };
  // ---------- Τέλος renderEventLayers ----------

  // 1. Initialize the map (μόνο μια φορά)
  useEffect(() => {
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: selectedMapStyle,
      center: [22.9527, 40.6253],
      zoom: 12,
    });

    mapRef.current = map;

    map.on("load", () => {
      mapLoadedRef.current = true;
      renderEventLayers(map); // πρώτη φορά που βάζουμε layers
    });

    return () => {
      map.remove();
      mapRef.current = null;
      mapLoadedRef.current = false;
    };
    
  }, []); // κενό dependency – ΔΕΝ ξαναφτιάχνουμε τον χάρτη

  // 2. Όποτε αλλάζουν τα events και ο χάρτης είναι έτοιμος → redraw layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    renderEventLayers(map);
  }, [events]);

  // 3. Fly-to / highlight επιλεγμένο event
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current || !selectedEvent || !selectedEvent.geometry)
      return;

    const geom = selectedEvent.geometry;
    let coordinates = null;

    if (geom.type === "Point") {
      coordinates = geom.coordinates[0];
    } else if (geom.type === "LineString") {
      const midIndex = Math.floor(geom.coordinates.length / 2);
      coordinates = geom.coordinates[midIndex];
    }

    if (coordinates) {
      map.flyTo({
        center: coordinates,
        zoom: 18,
        speed: 1.2,
        curve: 1,
        essential: true,
      });
    }

    // Reset previously selected feature state
    if (lastSelectedIdRef.current !== null) {
      map.setFeatureState(
        { source: "events-point", id: lastSelectedIdRef.current },
        { selected: false }
      );
      map.setFeatureState(
        { source: "events-lineStringSymbol", id: lastSelectedIdRef.current },
        { selected: false }
      );
    }

    const currentId = selectedEvent.id;
    lastSelectedIdRef.current = currentId;

    map.setFeatureState(
      { source: "events-point", id: currentId },
      { selected: true }
    );
    map.setFeatureState(
      { source: "events-lineStringSymbol", id: currentId },
      { selected: true }
    );
  }, [selectedEvent]);

  // 4. Αλλαγή 2D/3D style – re-add layers μετά το style.load
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    mapLoadedRef.current = false;
    map.setStyle(selectedMapStyle);

    map.once("style.load", () => {
      mapLoadedRef.current = true;
      renderEventLayers(map); // re-add layers με τα τρέχοντα events
    });
  }, [selectedMapStyle]); // μόνο όταν όντως αλλάζει style

  return (
    <div style={{ position: "relative", width: "100%", height: "90vh" }}>
      {/* Toggle Switch */}
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          background: "#202940",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "8px",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              className="custom-switch-3D"
              checked={selectedMapStyle === map3D}
              onChange={() =>
                setSelectedMapStyle((prev) => (prev === map3D ? map2D : map3D))
              }
            />
          }
          label={selectedMapStyle === map3D ? "3D ON" : "3D OFF"}
        />
      </div>

      <div
        id="events-map"
        ref={mapContainerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export default MapComponent;
